import {
  HomeCard,
  HomeState,
  Questionnaire,
  SurveyBookingStatus,
  appRoutes,
  confirmSignDialogMessages,
  homeToDoActivitiesCardMessages,
  mifidViewDialogMessages,
  resetAmlSurvey,
  useAppDispatch,
  useAppSelector
} from 'azshared';
import { PraticaSelf } from 'azshared/build/main/lib/api/generated/dob';
import React, { JSX, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { AppButton } from '../../../../components/buttons/app-button';
import { AppLink } from '../../../../components/labels/app-link';
import { AppSvgIcon } from '../../../../components/svg-icon/app-svg-icon';
import { CompilationSelectionDialog } from '../../activity-area/components/compilation-selection-dialog/compilation-selection-dialog';
import { StartWebCollaborationDialog } from '../../activity-area/components/start-web-collaboration-dialog/start-web-collaboration-dialog';
import './home-to-do-activities-card.scss';
import { SpecimenUploadDialog } from '../../activity-area/components/specimen-upload-dialog/specimen-upload-dialog';
import {
  GetTasksResponse,
  GetTasksResponseAmlAndFatcaCrsStateEnum,
  GetTasksResponseMifidStateEnum,
  SpecimenStateStatusEnum
} from 'azshared/src/lib/api/generated';
import { ModalDialog } from 'src/components/modal/modal-dialog';
import { useGlobalActions } from 'src/hooks/global-actions';

interface HomeToDoActivitiesCardInterface {
  toDoActivities: HomeCard<GetTasksResponse>;
  practiceData: HomeCard<PraticaSelf>;
  openSelectionDialog: boolean;
  openSpecimenUploadDialog: boolean;
  handleSelectionDialog: (open: boolean) => void;
  handleSpecimenUploadDialog: (open: boolean) => void;
  handleSpecimenUploadSuccess: () => void;
  onModalitySelected: (type: Questionnaire, mode?: SurveyBookingStatus) => void;
  startWebCollaborationDialogOpen: boolean;
  startWebCollaboration: () => void;
  onCloseStartWebCollaborationDialog: () => void;
  confirmSignDialogOpen: boolean;
  openConfirmSignDialog: () => void;
  closeConfirmSignDialog: () => void;
  onConfirmSign: () => void;
  mifidViewDialogOpen: boolean;
  openMifidViewDialog: () => void;
  closeMifidViewDialog: () => void;
  onMifidView: () => void;
  onMifidModify: () => void;
}

export const HomeToDoActivitiesCard = (props: HomeToDoActivitiesCardInterface) => {
  const { formatMessage } = useIntl();
  const { navigate } = useGlobalActions();
  const dispatch = useAppDispatch();

  const {
    practiceData,
    toDoActivities,
    openSelectionDialog,
    openSpecimenUploadDialog,
    handleSelectionDialog,
    handleSpecimenUploadDialog,
    handleSpecimenUploadSuccess,
    confirmSignDialogOpen,
    openConfirmSignDialog,
    closeConfirmSignDialog,
    onConfirmSign,
    mifidViewDialogOpen,
    openMifidViewDialog,
    closeMifidViewDialog,
    onMifidView,
    onMifidModify,
    onModalitySelected,
    startWebCollaborationDialogOpen,
    startWebCollaboration,
    onCloseStartWebCollaborationDialog
  } = props;

  const { meetingDate, compileType, flagProfilaturaMifid, processStateCode } = practiceData?.value || {};

  const { startSigningProcessRequestStatus } = useAppSelector((state) => state.features.home as HomeState);

  const onClick = useCallback(() => {
    toDoActivities.value?.mifidState === GetTasksResponseMifidStateEnum.Completed ? openMifidViewDialog() : handleSelectionDialog(true);
  }, [handleSelectionDialog, openMifidViewDialog, toDoActivities.value?.mifidState]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick();
      }
    },
    [onClick]
  );

  const onClose = useCallback(() => handleSelectionDialog(false), [handleSelectionDialog]);
  const onLink = useCallback((e) => e.preventDefault(), []);

  const handleAmlClick = useCallback(
    async (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      dispatch(resetAmlSurvey());
      navigate(appRoutes.AmlFatcaCrsSurveyPage);
    },
    [navigate, dispatch]
  );

  const onSpecimenClose = useCallback(() => handleSpecimenUploadDialog(false), [handleSpecimenUploadDialog]);
  const onSpecimenUploadClick = useCallback(() => handleSpecimenUploadDialog(true), [handleSpecimenUploadDialog]);

  const handleKeyDownSpecimenUpload = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSpecimenUploadClick();
      }
    },
    [onSpecimenUploadClick]
  );

  const isSigned = useMemo(() => processStateCode > 2, [processStateCode]);
  const isToSign = useMemo(() => processStateCode === 2, [processStateCode]);

  const enableFacta = useMemo(
    () =>
      !isSigned &&
      !isToSign &&
      toDoActivities.value?.amlAndFatcaCrsState !== GetTasksResponseAmlAndFatcaCrsStateEnum.NotFillable &&
      toDoActivities.error !== 'GENERIC_ERROR',
    [isToSign, isSigned, toDoActivities.error, toDoActivities.value?.amlAndFatcaCrsState]
  );

  const enableMifid = useMemo(
    () =>
      !isSigned &&
      !isToSign &&
      toDoActivities.value?.mifidState !== GetTasksResponseMifidStateEnum.NotFillable &&
      toDoActivities.value?.amlAndFatcaCrsState === GetTasksResponseAmlAndFatcaCrsStateEnum.Completed,
    [isToSign, isSigned, toDoActivities.value?.amlAndFatcaCrsState, toDoActivities.value?.mifidState]
  );

  const signButtonDisabled = useMemo(() => {
    return (
      (!isToSign && !isSigned && toDoActivities.value?.amlAndFatcaCrsState !== GetTasksResponseAmlAndFatcaCrsStateEnum.Completed) ||
      (flagProfilaturaMifid && toDoActivities.value?.mifidState !== GetTasksResponseMifidStateEnum.Completed) ||
      toDoActivities.value?.specimenState?.status !== SpecimenStateStatusEnum.Completed
    );
  }, [
    isToSign,
    isSigned,
    flagProfilaturaMifid,
    toDoActivities.value?.amlAndFatcaCrsState,
    toDoActivities.value?.mifidState,
    toDoActivities.value?.specimenState?.status
  ]);

  const goToSign = useCallback(() => navigate(appRoutes.Sign), [navigate]);

  return (
    <div className='home-to-do-activities-card'>
      <CompilationSelectionDialog
        meetingDate={meetingDate}
        compileType={compileType}
        attemptsExpired={practiceData?.value?.attemptsLeftCounter === 0}
        onPressButton={onModalitySelected}
        open={openSelectionDialog}
        onClose={onClose}
      />

      <SpecimenUploadDialog open={openSpecimenUploadDialog} onSuccessConfirm={handleSpecimenUploadSuccess} onClose={onSpecimenClose} />

      <ModalDialog
        open={confirmSignDialogOpen}
        onConfirm={onConfirmSign}
        onClose={closeConfirmSignDialog}
        onCancel={closeConfirmSignDialog}
        title={formatMessage(confirmSignDialogMessages.cardTitle)}
        cancelButtonLabel={formatMessage(confirmSignDialogMessages.cancelCTA)}
        confirmButtonLabel={formatMessage(confirmSignDialogMessages.confirmCTA)}
      />

      <ModalDialog
        open={mifidViewDialogOpen}
        onConfirm={onMifidModify}
        onClose={closeMifidViewDialog}
        onCancel={onMifidView}
        title={formatMessage(mifidViewDialogMessages.cardTitle)}
        cancelButtonLabel={formatMessage(mifidViewDialogMessages.cancelCTA)}
        confirmButtonLabel={formatMessage(mifidViewDialogMessages.confirmCTA)}
      />

      <StartWebCollaborationDialog onContinue={startWebCollaboration} open={startWebCollaborationDialogOpen} onClose={onCloseStartWebCollaborationDialog} />

      <div className='home-to-do-activities-card-header'>
        <AppSvgIcon icon='puzzle-grey' />
        <h4>{formatMessage(homeToDoActivitiesCardMessages.title)}</h4>
      </div>

      <div className='home-to-do-activities-card-body'>
        <div className='grid-item padding'>{formatMessage(homeToDoActivitiesCardMessages.questionnaireAMLandFATCA)}</div>
        {!enableFacta && (
          <>
            <div className='grid-item arrow-grey padding'>
              <AppSvgIcon icon='arrow-grey' />
            </div>
            <div className='grid-item home-to-do-activities-card-body-go-to-questionnaire padding disabled'>
              {formatMessage(homeToDoActivitiesCardMessages.goToQuestionnaire)}
            </div>
          </>
        )}

        {enableFacta && (
          <>
            <div className='grid-item arrow-blue padding'>
              <AppSvgIcon icon='arrow-blue' />
            </div>
            <div className='grid-item home-to-do-activities-card-body padding'>
              <AppLink
                to={appRoutes.AmlFatcaCrsSurveyPage}
                replace={false}
                label={formatMessage(homeToDoActivitiesCardMessages.goToQuestionnaire)}
                onClick={handleAmlClick}
              />
            </div>
          </>
        )}

        <div className='grid-item padding'>
          <QuestionnaireStatusDot status={toDoActivities.value?.amlAndFatcaCrsState} />
        </div>

        {flagProfilaturaMifid && (
          <>
            <div className='grid-item'>{formatMessage(homeToDoActivitiesCardMessages.questionnaireMIFID)}</div>
            {!enableMifid && (
              <div className='grid-item arrow-grey'>
                <AppSvgIcon icon='arrow-grey' />
              </div>
            )}
            {enableMifid && (
              <div className='grid-item arrow-blue'>
                <AppSvgIcon icon='arrow-blue' />
              </div>
            )}

            {!enableMifid && (
              <div className='grid-item home-to-do-activities-card-body-go-to-questionnaire disabled'>
                {formatMessage(homeToDoActivitiesCardMessages.goToQuestionnaire)}
              </div>
            )}
            {enableMifid && (
              <div className={'grid-item home-to-do-activities-card-body-go-to-questionnaire'} onClick={onClick} onKeyDown={handleKeyDown} tabIndex={0}>
                <AppLink
                  to={appRoutes.MifidSurveyPage}
                  replace={false}
                  label={formatMessage(homeToDoActivitiesCardMessages.goToQuestionnaire)}
                  onClick={onLink}
                />
              </div>
            )}
            <div className='grid-item padding'>
              <QuestionnaireStatusDot status={toDoActivities.value?.mifidState} />
            </div>
          </>
        )}
        <>
          <div className='grid-item'>{formatMessage(homeToDoActivitiesCardMessages.uploadSpecimenTitle)}</div>
          {(isToSign || isSigned) && (
            <div className='grid-item arrow-grey'>
              <AppSvgIcon icon='arrow-grey' />
            </div>
          )}
          {!isToSign && !isSigned && (
            <div className='grid-item arrow-blue'>
              <AppSvgIcon icon='arrow-blue' />
            </div>
          )}

          {(isToSign || isSigned) && (
            <div className='grid-item home-to-do-activities-card-body-go-to-questionnaire disabled'>
              {formatMessage(homeToDoActivitiesCardMessages.uploadSpecimen)}
            </div>
          )}
          {!isToSign && !isSigned && (
            <div
              className='grid-item home-to-do-activities-card-body-go-to-questionnaire'
              onClick={onSpecimenUploadClick}
              onKeyDown={handleKeyDownSpecimenUpload}
              tabIndex={0}>
              <AppLink to={appRoutes.MifidSurveyPage} replace={false} label={formatMessage(homeToDoActivitiesCardMessages.uploadSpecimen)} onClick={onLink} />
            </div>
          )}
          <div className='grid-item'>
            <QuestionnaireStatusDot status={toDoActivities.value?.specimenState?.status} />
          </div>
        </>
      </div>

      <div className='home-to-do-activities-card-satus'>
        <div className='grid-item'>
          <AppSvgIcon icon='single-dot-grey' />
          {formatMessage(homeToDoActivitiesCardMessages.notFillable)}
        </div>
        <div className='grid-item'>
          <AppSvgIcon icon='single-dot-yellow' />
          {formatMessage(homeToDoActivitiesCardMessages.toComplete)}
        </div>
        <div className='grid-item'>
          <AppSvgIcon icon='single-dot-green' />
          {formatMessage(homeToDoActivitiesCardMessages.completed)}
        </div>
      </div>

      {!isSigned && (
        <div className='home-to-do-activities-card-body-button'>
          <AppButton
            label={formatMessage(isToSign ? homeToDoActivitiesCardMessages.goToSign : homeToDoActivitiesCardMessages.sign)}
            onClick={isToSign ? goToSign : openConfirmSignDialog}
            loading={startSigningProcessRequestStatus === 'loading'}
            disabled={signButtonDisabled}
          />
        </div>
      )}
    </div>
  );
};

interface QuestionnaireStatusDotInterface {
  status: GetTasksResponseAmlAndFatcaCrsStateEnum | GetTasksResponseMifidStateEnum | SpecimenStateStatusEnum;
}

const QuestionnaireStatusDot = (props: QuestionnaireStatusDotInterface): JSX.Element => {
  const { status } = props;

  switch (status) {
    case 'NotFillable':
      return <AppSvgIcon icon='single-dot-grey' />;
    case 'ToComplete':
      return <AppSvgIcon icon='single-dot-yellow' />;
    case 'Completed':
      return <AppSvgIcon icon='single-dot-green' />;
  }

  return <></>;
};
